import { Box, Heading } from '@zillow/constellation-10';
import type { TourDuration } from '@zg-rentals/ts-z-calendar-client';

import AvailabilityEventEditModal from '../AvailabilityEventEditModal';
import AvailabilityEventPreviewModal from '../AvailabilityEventPreviewModal';
import TourEventModal from '../TourEventModal';
import type {
  AvailabilityEvent,
  CalendarEventHandlers,
  TourEvent,
  ZCalendarEvent,
} from '../../../../constants/TourCalendar';
import { Z_CALENDAR_EVENT_TYPE } from '../../../../constants/TourCalendar';
import {
  getIsAvailabilityEvent,
  getIsInProgressAvailabilityEvent,
  getIsTourEvent,
} from '../../../../utils/calendarEventGetters';

const getPopoverHeadingText = (event: ZCalendarEvent) => {
  if (getIsAvailabilityEvent(event)) {
    return 'Available';
  }
  if (getIsInProgressAvailabilityEvent(event)) {
    return 'Add a time frame';
  }
  if (getIsTourEvent(event)) {
    return 'Manage tour';
  }

  return '';
};

type CalendarEventModalProps = {
  canEditTourDuration?: boolean;
  children?: JSX.Element;
  displayEventEditPopover: boolean;
  event: ZCalendarEvent;
  handlers: CalendarEventHandlers;
  isOpen?: boolean;
  tourDuration: TourDuration;
};

const CalendarEventModal = ({
  canEditTourDuration = false,
  children,
  displayEventEditPopover,
  event,
  handlers,
  isOpen = true,
  tourDuration,
}: CalendarEventModalProps) => {
  const {
    onClose,
    onSaveClick,
    onDeleteClick,
    onEditClick,
    onDeclineClick,
    onConfirmClick,
    onCancelTourConfirmationClick,
  } = handlers;

  const header = (
    <Box css={{ display: 'flex', justifyContent: 'flexStart' }}>
      <Heading data-testid="CalendarEventModal-Header" level={5}>
        {getPopoverHeadingText(event)}
      </Heading>
    </Box>
  );

  if (getIsAvailabilityEvent(event) || getIsInProgressAvailabilityEvent(event)) {
    if (displayEventEditPopover) {
      return (
        <AvailabilityEventEditModal
          canEditTourDuration={canEditTourDuration}
          event={event as AvailabilityEvent}
          header={header}
          isOpen={isOpen}
          onClose={onClose}
          onSaveClick={onSaveClick}
          tourDuration={tourDuration}
        >
          {children}
        </AvailabilityEventEditModal>
      );
    }

    return (
      <AvailabilityEventPreviewModal
        event={event as AvailabilityEvent}
        header={header}
        isOpen={isOpen}
        onClose={onClose}
        onDeleteClick={onDeleteClick}
        onEditClick={onEditClick}
      >
        {children}
      </AvailabilityEventPreviewModal>
    );
  }

  if (event.eventType === Z_CALENDAR_EVENT_TYPE.TOUR) {
    return (
      <TourEventModal
        event={event as TourEvent}
        header={header}
        isOpen={isOpen}
        onCancelTourConfirmationClick={onCancelTourConfirmationClick}
        onClose={onClose}
        onConfirmClick={onConfirmClick}
        onDeclineClick={onDeclineClick}
      >
        {children}
      </TourEventModal>
    );
  }
};

export default CalendarEventModal;
